import { sizes, type GradientKey } from '@mybusinessmate/ui-tokens';
import { cx } from '../../lib/cx';

export type AvatarSize = keyof typeof sizes.avatar;

export interface AvatarProps {
  /** Image URL. Falls back to initials, then to an empty brand disc. */
  src?: string;
  initials?: string;
  size?: AvatarSize;
  /** Use the brand gradient instead of the solid brand fill (Figma header "Avatar Button"). */
  gradient?: boolean;
  /** `neutral` is the light-grey disc with dark initials used for people in staff tables (Figma A03.1, A04). */
  tone?: 'brand' | 'neutral';
  /** Accessible name; the avatar is decorative (aria-hidden) when omitted. */
  label?: string;
  className?: string;
}

const SIZE: Record<AvatarSize, string> = {
  xxs: 'size-(--mbm-size-avatar-xxs)',
  xs: 'size-(--mbm-size-avatar-xs)',
  sm: 'size-(--mbm-size-avatar-sm)',
  md: 'size-(--mbm-size-avatar-md)',
  lg: 'size-(--mbm-size-avatar-lg)',
  xl: 'size-(--mbm-size-avatar-xl)',
};

// Referenced so the gradient key stays typed against the token file.
const BRAND_GRADIENT: GradientKey = 'brand';

/** Figma "Avatar Button" (21:141). */
export function Avatar({
  src,
  initials,
  size = 'md',
  gradient = false,
  tone = 'brand',
  label,
  className,
}: AvatarProps) {
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-gradient={gradient ? BRAND_GRADIENT : undefined}
      className={cx(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        SIZE[size],
        tone === 'neutral'
          ? cx(
              'bg-surface-field text-text-primary',
              size === 'xl' || size === 'lg' ? 'text-heading-h3' : 'text-label-small',
            )
          : cx(
              'border-2 border-surface-card text-label-medium text-text-on-dark',
              gradient ? 'bg-(image:--mbm-gradient-brand)' : 'bg-brand-primary',
            ),
        className,
      )}
    >
      {src ? (
        <img src={src} alt="" className="size-full object-cover" />
      ) : initials ? (
        initials.slice(0, 2).toUpperCase()
      ) : null}
    </span>
  );
}
